import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Star, 
  MapPin, 
  CheckCircle2,
  Shield,
  Monitor,
  Users,
  Clock,
  AlertTriangle,
  PartyPopper,
  Puzzle,
  Gamepad2
} from 'lucide-react';

/**
 * Board Games & Puzzles Service Cards
 * Based on wireframe specifications with verification levels
 */

// Card A: Anchor Card - Board Game Teaching / Tutorials
export const GameTeachingCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
  hasOnlineOption = true,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-amber-200 bg-amber-50/20'
      }`}
      data-testid="game-teaching-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Game Service</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🎲</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Board Game Teaching</h3>
            <p className="text-xs text-gray-500">Rules · Strategy · Beginner-friendly</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-amber-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Learn complex games easily</span>
          </p>
          <p className="text-xs text-amber-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Online or in-person</span>
          </p>
        </div>

        {/* Rating & Location */}
        <div className="flex items-center justify-between text-xs mb-2">
          <div className="flex items-center gap-1">
            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{provider?.rating?.toFixed(1) || '4.8'}</span>
            <span className="text-gray-500">({provider?.review_count || 260} sessions)</span>
          </div>
          <span className="text-gray-500 flex items-center gap-1">
            {hasOnlineOption && <Monitor className="h-3 w-3" />}
            <span>{hasOnlineOption ? 'Online' : ''}{hasOnlineOption && provider?.travel_radius_km ? ' / ' : ''}{provider?.travel_radius_km ? `${provider.travel_radius_km}km` : ''}</span>
          </span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 15}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-amber-600 hover:bg-amber-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-game-teaching"
        >
          Book session
        </Button>
      </CardContent>
    </Card>
  );
};

// Card B: Game Host / Game Master (High Value)
export const GameHostCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-purple-200 bg-purple-50/20'
      }`}
      data-testid="game-host-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Event Host</span>
          </div>
        )}

        {/* High Value Badge */}
        <Badge className="bg-purple-100 text-purple-700 text-xs mb-2">
          <PartyPopper className="h-3 w-3 mr-1" />
          Popular Choice
        </Badge>

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🎉</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Board Game Host / Game Master</h3>
            <p className="text-xs text-gray-500">Game nights · Parties · Events</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-purple-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Manages rules &amp; flow</span>
          </p>
          <p className="text-xs text-purple-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Fun for all players</span>
          </p>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.9'}</span>
          <span className="text-gray-500">({provider?.review_count || 140} events)</span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 45}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-purple-600 hover:bg-purple-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-game-host"
        >
          Book host
        </Button>
      </CardContent>
    </Card>
  );
};

// Card C: Kids' Board Game Party Host (Trust-Focused)
export const KidsPartyHostCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
  isBackgroundChecked = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border-2 hover:shadow-md transition-all ${
        isBackgroundChecked 
          ? 'border-green-300 bg-green-50/20' 
          : 'border-gray-200 bg-gray-50'
      }`}
      data-testid="kids-party-host-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Kids' Event</span>
          </div>
        )}

        {/* Trust Badge - ALWAYS FIRST for kids' services */}
        <div className={`flex items-center gap-1 text-xs rounded px-2 py-1 mb-2 ${
          isBackgroundChecked ? 'bg-green-100 text-green-700' : 'bg-amber-50 text-amber-600'
        }`}>
          <Shield className="h-3 w-3" />
          <span>{isBackgroundChecked ? 'Background checked' : 'Verification pending'}</span>
        </div>

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🧒</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Kids' Board Game Party Host</h3>
            <p className="text-xs text-gray-500">Age-appropriate · Supervised</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className={`text-xs flex items-center gap-1 ${isBackgroundChecked ? 'text-green-700' : 'text-gray-400'}`}>
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Structured fun &amp; learning</span>
          </p>
        </div>

        {/* Rating (only show if verified) */}
        {isBackgroundChecked && (
          <div className="flex items-center gap-1 text-xs mb-2">
            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{provider?.rating?.toFixed(1) || '4.9'}</span>
            <span className="text-gray-500">({provider?.review_count || 98} parties)</span>
          </div>
        )}

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 60}</span>
        </p>

        {/* CTA */}
        <Button 
          className={`w-full h-8 text-sm ${isBackgroundChecked ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400'}`}
          onClick={() => isBackgroundChecked && onBook?.(service, provider)}
          disabled={!isBackgroundChecked}
          data-testid="book-kids-party"
        >
          {isBackgroundChecked ? 'Book party' : 'Requires verification'}
        </Button>
      </CardContent>
    </Card>
  );
};

// Card D: Puzzle Coaching / Challenge Session
export const PuzzleCoachingCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
  hasOnlineOption = true,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-cyan-200 bg-cyan-50/20'
      }`}
      data-testid="puzzle-coaching-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Puzzle Coaching</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🧩</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Puzzle Strategy Session</h3>
            <p className="text-xs text-gray-500">Logic · Escape-style · Brain training</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-cyan-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Improve problem solving</span>
          </p>
          {hasOnlineOption && (
            <p className="text-xs text-cyan-700 flex items-center gap-1">
              <Monitor className="h-3 w-3 flex-shrink-0" />
              <span>Online available</span>
            </p>
          )}
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.7'}</span>
          <span className="text-gray-500">({provider?.review_count || 82} sessions)</span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 20}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-cyan-600 hover:bg-cyan-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-puzzle-coaching"
        >
          Book session
        </Button>
      </CardContent>
    </Card>
  );
};

// Bundle Card - Party & Event Bundles
export const BoardGameBundleCard = ({
  bundle,
  currency = '£',
  onBook,
  isPopular = false,
}) => {
  const getBundleStyle = () => {
    switch (bundle.id) {
      case 'game_night_starter':
        return { border: 'border-amber-300', bg: 'from-amber-50', accent: 'bg-amber-600', icon: '🎲' };
      case 'kids_birthday_party':
        return { border: 'border-green-300', bg: 'from-green-50', accent: 'bg-green-600', icon: '🧒' };
      case 'corporate_team_event':
        return { border: 'border-blue-300', bg: 'from-blue-50', accent: 'bg-blue-600', icon: '🏢' };
      case 'puzzle_challenge_night':
        return { border: 'border-cyan-300', bg: 'from-cyan-50', accent: 'bg-cyan-600', icon: '🧩' };
      default:
        return { border: 'border-purple-300', bg: 'from-purple-50', accent: 'bg-purple-600', icon: '🎉' };
    }
  };

  const style = getBundleStyle();

  return (
    <Card 
      className={`overflow-hidden border-2 ${style.border} bg-gradient-to-br ${style.bg} to-white hover:shadow-lg transition-all`}
      data-testid={`board-game-bundle-card-${bundle.id}`}
    >
      <CardContent className="p-4">
        {/* Popular Badge */}
        {isPopular && (
          <Badge className="bg-yellow-500 text-white text-xs mb-2">
            ⭐ Most Popular
          </Badge>
        )}

        {/* Savings Badge */}
        {bundle.savings && (
          <Badge className={`${style.accent} text-white text-xs mb-2`}>
            Save {currency}{bundle.savings}
          </Badge>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-3">
          <span className="text-2xl">{style.icon}</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">{bundle.name}</h3>
          </div>
        </div>

        {/* Included Items */}
        <div className="space-y-1 mb-3">
          {bundle.includes?.map((item, idx) => (
            <p key={idx} className="text-xs text-gray-700 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 flex-shrink-0 text-green-600" />
              <span>{item}</span>
            </p>
          ))}
        </div>

        {/* Age Group Badge */}
        {bundle.age_group && (
          <Badge variant="outline" className="text-xs mb-2">
            {bundle.age_group === 'kids' ? '🧒 Kids' : bundle.age_group === 'corporate' ? '🏢 Corporate' : '👥 Adults'}
          </Badge>
        )}

        {/* Pricing */}
        <div className="mb-3">
          <span className="font-bold text-lg">
            {currency}{bundle.price}
          </span>
          {bundle.duration && (
            <span className="text-gray-500 text-sm"> · {bundle.duration}</span>
          )}
        </div>

        {/* CTA */}
        <Button 
          className={`w-full ${style.accent} hover:opacity-90`}
          onClick={() => onBook?.(bundle)}
          data-testid={`book-bundle-${bundle.id}`}
        >
          {bundle.cta || 'Book bundle'}
        </Button>
      </CardContent>
    </Card>
  );
};

// Mobile Sticky Pill for Board Games
export const BoardGameMobilePill = ({
  startingPrice = 15,
  currency = '£',
  onTap,
}) => {
  return (
    <div 
      className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 lg:hidden"
      data-testid="board-game-mobile-pill"
    >
      <button
        onClick={onTap}
        className="flex items-center gap-2 px-4 py-3 bg-amber-600 text-white rounded-full shadow-lg hover:bg-amber-700 transition-all active:scale-95"
      >
        <span>🎲</span>
        <span className="text-sm font-medium">Game night help nearby</span>
        <span className="text-sm">From {currency}{startingPrice}</span>
      </button>
    </div>
  );
};

// Mobile Bottom Sheet Content
export const BoardGameMobileSheet = ({
  services,
  providers,
  onBook,
  currency = '£',
}) => {
  const serviceTypes = [
    { type: 'game_teaching', icon: '🎲', name: 'Learn a Game', defaultPrice: 15, label: 'sessions' },
    { type: 'game_host', icon: '🎉', name: 'Game Host', defaultPrice: 45, label: 'events' },
    { type: 'kids_party_host', icon: '🧒', name: 'Kids Party', defaultPrice: 60, label: 'parties' },
    { type: 'puzzle_coaching', icon: '🧩', name: 'Puzzle Coach', defaultPrice: 20, label: 'sessions' },
  ];

  return (
    <div className="p-4">
      <h3 className="font-bold text-lg mb-4">Board Games &amp; Puzzle Services Near You</h3>
      <div className="space-y-3">
        {serviceTypes.map((st) => {
          const service = services?.find(s => s.type === st.type);
          const provider = providers?.find(p => p.services?.some(s => s.type === st.type));
          const price = service?.price || st.defaultPrice;
          const rating = provider?.rating || 4.8;

          return (
            <div 
              key={st.type}
              className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{st.icon}</span>
                <div>
                  <p className="font-medium text-sm">{st.name}</p>
                  <p className="text-xs text-gray-500">From {currency}{price}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 text-xs">
                  <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                  <span>{rating.toFixed(1)}</span>
                </div>
                <Button 
                  size="sm" 
                  className="h-8 bg-amber-600 hover:bg-amber-700"
                  onClick={() => onBook?.(service || { type: st.type, name: st.name, price }, provider)}
                >
                  Book
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Post-Checkout Upsell Card for Board Games
export const BoardGamePostCheckoutCard = ({
  productType,
  prompt,
  service,
  provider,
  onAdd,
  onDismiss,
}) => {
  return (
    <Card 
      className="border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-white"
      data-testid="board-game-post-checkout-upsell"
    >
      <CardContent className="p-3 flex items-center gap-3">
        <span className="text-2xl">🎲</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{prompt?.title || 'Your game is on the way!'}</p>
          <p className="text-xs text-gray-500">{prompt?.subtitle || 'Want help learning it?'}</p>
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            className="text-xs h-8"
            onClick={onDismiss}
          >
            Not now
          </Button>
          <Button 
            size="sm" 
            className="text-xs h-8 bg-amber-600 hover:bg-amber-700"
            onClick={() => onAdd?.(service, provider)}
          >
            {prompt?.cta || 'Add session'} · {provider?.currency || '£'}{service?.price || 15}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Verification Level Banner
export const HostVerificationBanner = ({
  hostLevel,
  requiredLevel,
  serviceName,
  onLearnMore,
}) => {
  const levelNames = {
    0: 'None',
    1: 'Game Teaching (Adults)',
    2: 'Game Hosting (Adults)',
    3: "Kids' Party Hosting",
    4: 'Corporate / Public Events',
  };

  if (hostLevel >= requiredLevel) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4" data-testid="host-verification-banner">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-800">
            Verification Required
          </p>
          <p className="text-xs text-amber-600 mt-1">
            {serviceName} requires Level {requiredLevel} verification ({levelNames[requiredLevel]}).
            Current level: L{hostLevel}.
          </p>
          {onLearnMore && (
            <button 
              onClick={onLearnMore}
              className="text-xs text-amber-700 underline mt-1"
            >
              Learn about host verification
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Kids Safety Disclaimer
export const KidsSafetyDisclaimer = () => {
  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-2 mb-4 text-xs" data-testid="kids-safety-disclaimer">
      <div className="flex items-start gap-2">
        <Shield className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-green-800 font-medium">Child Safety Notice</p>
          <p className="text-green-700 mt-1">
            All hosts for children's events are background-checked. Parents/guardians should be present during all activities.
          </p>
        </div>
      </div>
    </div>
  );
};

export default {
  GameTeachingCard,
  GameHostCard,
  KidsPartyHostCard,
  PuzzleCoachingCard,
  BoardGameBundleCard,
  BoardGameMobilePill,
  BoardGameMobileSheet,
  BoardGamePostCheckoutCard,
  HostVerificationBanner,
  KidsSafetyDisclaimer,
};
